import React, { createContext, FC, useContext, useEffect, useMemo, useState } from "react";
import { Lemonade, Purchase, Sale, Unit, units } from "./internal/types";
import { db } from "./internal/db";

type LemonadeContextType = {
    lemonades: Lemonade[];
    sales: Sale[];
    purchases: Purchase[];
    ingredients: Set<string>;
    inventory: Inventory;
    totalMoney: number;
    addLemonade: (lemonade: Lemonade) => Promise<string | void>;
    addSale: (sale: Sale) => Promise<string | void>;
    addPurchase: (purchase: Purchase) => Promise<string | void>;
    getUnitsForIngredient: (ingredient: string) => Unit[];
    removeLemonade: (lemonade: Lemonade) => Promise<void>;
};

const LemonadeContext = createContext<LemonadeContextType | undefined>(undefined);

export const useLemonadeContext = () => {
    const ctx = useContext(LemonadeContext);
    if (!ctx) {
        throw new Error("useLemonadeContext must be used within a LemonadeProvider");
    }
    return ctx;
};

export const LemonadeProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lemonades, setLemonades] = useState<Lemonade[]>([]);
    const [sales, setSales] = useState<Sale[]>([]);
    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [ingredients, setIngredients] = useState<Set<string>>(new Set());
    const [inventory, setInventory] = useState<Inventory>({});
    const [totalMoney, setTotalMoney] = useState(0);

    const refresh = async () => {
        const newLemonades = await db.getLemonades();
        const newSales = await db.getSales();
        const newPurchases = await db.getPurchases();

        setLemonades(newLemonades);
        setSales(newSales);
        setPurchases(newPurchases);

        const inv: Inventory = {};

        // add purchases
        for (const purchase of newPurchases) {
            const name = purchase.product.toLowerCase();
            const group = getGroup(purchase.amount.unit);
            const siAmount = unitToSI[purchase.amount.unit](purchase.amount.amount);

            if (!inv[name]) inv[name] = { volume: 0, mass: 0, unit: 0 };
            inv[name][group] += siAmount;
        }

        // subtract sales
        for (const sale of newSales) {
            for (const ing of sale.lemonade.ingredients) {
                const name = ing.name.toLowerCase();
                const group = getGroup(ing.amount.unit);
                const siAmount = unitToSI[ing.amount.unit](ing.amount.amount) * sale.amount * 0.1; // sale.amount is in dl, convert to SI units (l)

                if (!inv[name]) inv[name] = { volume: 0, mass: 0, unit: 0 };
                inv[name][group] -= siAmount;
            }
        }

        setInventory(inv);

        // calculate total money by subtracting the total revenue from expenses
        const totalRevenue = newSales.reduce((sum, sale) => sum + sale.price, 0);
        const totalExpenses = newPurchases.reduce((sum, purchase) => sum + purchase.price, 0);
        setTotalMoney(totalRevenue - totalExpenses);
    };

    // calculate all ingredients from lemonades and purchases
    useEffect(() => {
        const allIngredients = new Set<string>();

        // add ingredients from lemonades
        for (const lemonade of lemonades) {
            for (const ingredient of lemonade.ingredients) {
                allIngredients.add(ingredient.name);
            }
        }

        // add products from purchases
        for (const purchase of purchases) {
            allIngredients.add(purchase.product);
        }

        setIngredients(allIngredients);
    }, [lemonades, purchases]);

    // initial load
    useEffect(() => {
        refresh();
    }, []);

    const addLemonade = async (lemonade: Lemonade) => {
        if (lemonades.find((l) => l.name.toUpperCase() === lemonade.name.toUpperCase())) {
            return `A "${lemonade.name}" nevű limonádé már létezik.`;
        }

        // make all ingredient names lowercase
        lemonade.ingredients = lemonade.ingredients.map((ingredient) => ({
            ...ingredient,
            name: ingredient.name.toLowerCase()
        }));

        // make lemonade's name lowercase (except first letter)
        lemonade.name = lemonade.name.charAt(0).toUpperCase() + lemonade.name.slice(1).toLowerCase();

        // calculate total volume (find all ingredients with volume unit)
        const totalVolume = lemonade.ingredients.reduce((sum, ing) => {
            if (getGroup(ing.amount.unit) === "volume") {
                return sum + unitToSI[ing.amount.unit](ing.amount.amount);
            }
            return sum;
        }, 0);

        // scale ingredients to 1 liter
        const scale = 1 / totalVolume;
        lemonade.ingredients = lemonade.ingredients.map((ing) => ({
            ...ing,
            amount: {
                amount: ing.amount.amount * scale,
                unit: ing.amount.unit
            }
        }));

        await db.addLemonade(lemonade);
        refresh();
    };

    const removeLemonade = async (lemonade: Lemonade) => {
        await db.removeLemonade(lemonade);
        refresh();
    };

    const addSale = async (sale: Sale) => {
        // check inventory for each ingredient
        for (const ing of sale.lemonade.ingredients) {
            const name = ing.name.toLowerCase();
            const group = getGroup(ing.amount.unit);
            // 0.1 because sale.amount is in dl, and we need to convert to SI units (l)
            const siAmount = unitToSI[ing.amount.unit](ing.amount.amount) * sale.amount * 0.1;
            const available = inventory[name]?.[group] ?? 0;
            const siUnit = groupToSI[group];
            if (available < siAmount) {
                return `Nincs elég "${ing.name}" (${roundNumber(siAmount, 2)} ${siUnit} szükséges, csak ${roundNumber(available, 2)} ${siUnit} van készleten).`;
            }
        }
        await db.addSale(sale);
        refresh();
    };

    const addPurchase = async (purchase: Purchase) => {
        await db.addPurchase(purchase);
        refresh();
    };

    const getUnitsForIngredient = (ingredient: string): Unit[] => {
        if (ingredient == null) {
            return [];
        }

        // first find the groups
        const groups = new Set<InventoryGroup>();
        for (const purchase of purchases) {
            if (purchase.product.toLowerCase() === ingredient.toLowerCase()) {
                groups.add(getGroup(purchase.amount.unit));
            }
        }
        for (const lemonade of lemonades) {
            for (const ing of lemonade.ingredients) {
                if (ing.name.toLowerCase() === ingredient.toLowerCase()) {
                    groups.add(getGroup(ing.amount.unit));
                }
            }
        }

        if (groups.size === 0) {
            return Array.from(units);
        }

        
        // then return the units for those groups
        return units.filter((unit) => groups.has(getGroup(unit)));
    };

    const contextValue = useMemo(
        () => ({
            lemonades,
            sales,
            purchases,
            ingredients,
            inventory,
            totalMoney,
            addLemonade,
            addSale,
            addPurchase,
            getUnitsForIngredient,
            removeLemonade
        }),
        [lemonades, sales, purchases, ingredients, inventory, totalMoney]
    );

    return <LemonadeContext.Provider value={contextValue}>{children}</LemonadeContext.Provider>;
};

type InventoryGroup = "volume" | "mass" | "unit";
type InventoryEntry = {
    volume: number; // liters
    mass: number; // kilograms
    unit: number; // db
};
type Inventory = Record<string, InventoryEntry>;

/**
 * Get the inventory group for a given unit.
 *
 * @param unit The unit to check.
 * @returns The inventory group ("volume", "mass", or "unit").
 */
function getGroup(unit: Unit): InventoryGroup {
    if (["ml", "dl", "l"].includes(unit)) return "volume";
    if (["g", "dkg", "kg"].includes(unit)) return "mass";
    return "unit";
}

/**
 * Conversion factors to SI units.
 */
const unitToSI = {
    ml: (v: number) => v / 1000,
    dl: (v: number) => v / 10,
    l: (v: number) => v,
    g: (v: number) => v / 1000,
    dkg: (v: number) => v / 100,
    kg: (v: number) => v,
    db: (v: number) => v
} satisfies { [key in Unit]: (v: number) => number };

export const groupToSI = {
    mass: "kg",
    volume: "l",
    unit: "db"
} as const satisfies Record<InventoryGroup, string>;

export function roundNumber(num: number, digits: number): string {
    return new Intl.NumberFormat("hu-HU", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
    }).format(num);
}
